import type { TFunction } from 'i18next';
import type { ExtendedInitiative } from '../../../../../api/initiative-client/initiative-client.type';
import type { Team } from '../../../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../../../api/tasks-client/task-client.type';
import { formatCurrency } from '../../../../../helpers/currency-helpers';
import { getContributingTeams } from '../../../initiative-list/main-section/main-section.helpers';
import { formatInitiativeDate } from '../../../strategy.helpers';

/**
 * Retrieves formatted information about an epic for display
 *
 * @param {Epic} epic - The epic to get information for
 * @param {ExtendedInitiative | null} initiative - The initiative that the epic belongs to
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {string} currencyCode - Currency code for cost formatting (default 'USD')
 * @returns {Array<{label: string, value: string | number | null | undefined}>} An array of label-value pairs with epic information
 */
const getEpicInfo = (
  epic: Epic,
  initiative: ExtendedInitiative | null,
  t: TFunction,
  currencyCode = 'USD',
): { label: string; value: string | number | null | undefined }[] => {
  return [
    {
      label: t('common.type'),
      value: t('common.epic'),
    },
    {
      label: t('common.created'),
      value: formatInitiativeDate(epic.created_date, t),
    },
    {
      label: t('strategy.initiative_performance.cost_to_date'),
      value: formatCurrency(initiative?.costs_to_date?.epics?.[epic.id], { currency: currencyCode }),
    },
    {
      label: t('common.teams'),
      value: initiative
        ? getContributingTeams(initiative, epic.projects as Team[])
        : t('strategy.initiatives.labels.no_data', '-'),
    },
  ];
};

/**
 * Calculates the completion percentage of an epic based on its subtasks
 *
 * @param {Epic} epic - The epic to calculate completion percentage for
 * @returns {number} The percentage of completed subtasks, rounded to the nearest integer (0-100)
 */
const getCompletionPercentage = (epic: Epic): number => {
  return epic.subtasks_completed_count && epic.subtasks_count
    ? Math.round((epic.subtasks_completed_count / epic.subtasks_count) * 100)
    : 0;
};

export { getCompletionPercentage, getEpicInfo };
